import { z } from "zod";

const OpaqueIdSchema = z.uuid();
const RequestIdSchema = z.string().regex(/^req_[0-9a-f-]{36}$/u);
const Base64UrlSchema = z.string().regex(/^[A-Za-z0-9_-]+$/u);

export const WebPushConfigResponseSchema = z
  .object({
    available: z.boolean(),
    public_key: Base64UrlSchema.min(80).max(100).nullable(),
    request_id: RequestIdSchema,
  })
  .strict();

export const WebPushSubscriptionInputSchema = z
  .object({
    expected_version: z.number().int().positive().nullable(),
    endpoint: z
      .url()
      .max(2_048)
      .refine((value) => new URL(value).protocol === "https:", {
        message: "The Web Push endpoint must use HTTPS.",
      }),
    expiration_time: z.number().int().positive().nullable(),
    keys: z
      .object({
        p256dh: Base64UrlSchema.min(80).max(120),
        auth: Base64UrlSchema.min(16).max(64),
      })
      .strict(),
  })
  .strict();

export const WebPushSubscriptionSchema = z
  .object({
    id: OpaqueIdSchema,
    workspace_id: OpaqueIdSchema,
    device_id: OpaqueIdSchema,
    status: z.enum(["active", "revoked", "permanent_failure"]),
    expiration_time: z.number().int().positive().nullable(),
    last_success_at: z.number().int().nonnegative().nullable(),
    last_failure_at: z.number().int().nonnegative().nullable(),
    failure_code: z.string().min(1).max(160).nullable(),
    version: z.number().int().positive(),
    created_at: z.number().int().nonnegative(),
    updated_at: z.number().int().nonnegative(),
  })
  .strict();

export const WebPushSubscriptionResponseSchema = z
  .object({
    subscription: WebPushSubscriptionSchema.nullable(),
    idempotency_replayed: z.boolean(),
    request_id: RequestIdSchema,
  })
  .strict();

export const WebPushSubscriptionDeleteRequestSchema = z
  .object({ expected_version: z.number().int().positive() })
  .strict();

export type WebPushSubscriptionInput = z.infer<typeof WebPushSubscriptionInputSchema>;
export type WebPushSubscription = z.infer<typeof WebPushSubscriptionSchema>;
